/**
 * Named gesture-tuning presets for Comandero (POS / restaurant).
 *
 * Centralising the recognizer's `fling`/`thresholdPx` together with the
 * consumer-facing layout constants (commit thresholds, rubber-band limits,
 * animation durations) keeps the feel consistent across the two nuclear swipe
 * surfaces — the POS ticket line and the KDS line — and makes tuning a one-line
 * change.
 */

import type { FlingConfig } from "./types";

const GESTURE_DEADZONE_PX = 8;

const STANDARD_FLING: FlingConfig = {
  maxDurationMs: 300,
  minDistancePx: 50,
  axisRatio: 2,
};

/**
 * POS ticket line: swipe-left to remove the line from the order. Single-action
 * (left only) — committing on a destructive action, so the threshold is a touch
 * higher than a toggle to avoid accidental removals during a busy service.
 */
export const TICKET_LINE_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 96,
  rubberBandLimitPx: 130,
  rubberBandFactor: 0.25,
  hapticMs: 15,
  commitAnimationMs: 240,
  cancelAnimationMs: 220,
  fallbackWidthPx: 400,
  fling: { ...STANDARD_FLING },
} as const;

/**
 * KDS line: swipe-right to advance the cooking status (pending → cooking →
 * ready → served). Non-destructive forward bump, so a slightly lower commit
 * threshold keeps the kitchen flow fast.
 */
export const KDS_LINE_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 84,
  rubberBandLimitPx: 130,
  rubberBandFactor: 0.25,
  hapticMs: 12,
  commitAnimationMs: 220,
  cancelAnimationMs: 200,
  fallbackWidthPx: 360,
  fling: { ...STANDARD_FLING },
} as const;

/**
 * Menu category carousel (POS product grid): swipe horizontally to page between
 * category tabs. A bidirectional carousel — the current page tracks the finger
 * and, on commit (or fling), slides out while the neighbour slides in. The
 * commit distance is the smaller of an absolute cap and a fraction of the page
 * width, so it scales from a phone to a wide tablet. Edge over-drag (no
 * neighbour) rubber-bands instead of paging.
 */
export const MENU_CATEGORY_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 72,
  commitThresholdRatio: 0.28,
  rubberBandFactor: 0.3,
  hapticMs: 12,
  transitionMs: 260,
  fallbackWidthPx: 420,
  fling: { ...STANDARD_FLING },
} as const;

/**
 * Toast swipe-to-dismiss: drag/fling a toast horizontally (either direction)
 * past the commit threshold to dismiss it, otherwise it snaps back. A softer,
 * looser feel than the destructive ticket swipe (lower commit, higher
 * rubber-band travel) since dismissing a notification is forgiving. A shorter
 * fling `minDistancePx` makes a quick flick enough to clear it.
 */
export const TOAST_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 80,
  rubberBandLimitPx: 140,
  rubberBandFactor: 0.55,
  hapticMs: 12,
  dismissAnimationMs: 220,
  snapBackMs: 200,
  fling: { maxDurationMs: 300, minDistancePx: 40, axisRatio: 2 },
} as const;

/**
 * Mobile navigation drawer: drag it toward its own edge to dismiss it.
 *
 * The drawer is anchored to the left, so only a leftward drag means anything.
 * Dragging the other way has nothing to reveal — it gets a short, capped
 * rubber-band so the panel acknowledges the finger without ever detaching from
 * its edge or committing anything.
 */
export const NAV_DRAWER_DISMISS = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Travel toward the edge that commits the close on release. */
  commitThresholdPx: 72,
  /** Give on the closed-direction drag (the one that does nothing). */
  rubberBandFactor: 0.25,
  /** Hard cap on that give. */
  rubberBandLimitPx: 24,
  hapticMs: 12,
  /** Used for the backdrop fade when the panel has not been measured yet. */
  fallbackWidthPx: 280,
  /** A flick dismisses without reaching `commitThresholdPx`. */
  fling: { maxDurationMs: 300, minDistancePx: 40, axisRatio: 2 } satisfies FlingConfig,
} as const;
